import * as R from 'ramda';
import { pure, compose, lifecycle, withHandlers } from 'react-recompose';
// components
import { createMainCharge } from '../settings';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hocs
import { getInvoiceGlCode, withComponentDidUpdatePropCallback } from '../../../hocs';
// component charge
import { recalculateAllCharges } from '../formik/helpers';
import {
  setGlCodeToCharges,
  withRecalculateChargesOnChangeTotals,
  withRecalculateChargesOnChangeRecalculateId,
} from './';
//////////////////////////////////////////////////

const withRecalculateAllCharges = compose(
  withHandlers({
    handleRecalculateAllCharges: () => (propName, props) => {
      const { rateProps } = props;

      const { values, setValues } = rateProps;
      const { charges } = values;

      const newValues = R.assoc(GC.FIELD_CHARGES, recalculateAllCharges(charges, props), values);

      setValues(newValues);
    },
  }),
  withRecalculateChargesOnChangeTotals,
  withRecalculateChargesOnChangeRecalculateId,
  pure,
);

const withGLCodeMappings = compose(
  withHandlers({
    handleSetInitialGLCodes: (props) => () => {
      const { rateProps, isInvoice, isEditMode, glCodeMappings } = props;

      const { values, setValues } = rateProps;
      const { charges } = values;

      if (R.not(isInvoice)) return;

      if (R.and(isInvoice, G.isTrue(isEditMode))) return;

      const invoiceCurrency = G.getCurrencyFromObject2(values);
      const newValues = R.assoc(GC.FIELD_CHARGES, setGlCodeToCharges(charges, glCodeMappings, invoiceCurrency), values);

      setValues(newValues);
    },
    handleChangeCurrency: (props) => (propName, newProps) => {
      const { rateProps, isInvoice, glCodeMappings, glCodeMappingList } = props;

      const { values, setValues } = rateProps;
      const { charges } = values;

      if (R.not(isInvoice)) return;

      const { scopeToUse, orderTypeGuid } = glCodeMappings;

      const invoiceCurrency = G.getPropFromObject(propName, newProps);

      const invoiceGlCode = getInvoiceGlCode({
        scopeToUse,
        orderTypeGuid,
        glCodeMappings,
        invoiceCurrency,
        glCodeMappingList,
      });

      const newValues = {
        ...values,
        [GC.FIELD_GL_CODE]: G.fromNullToEmptyString(invoiceGlCode),
        [GC.FIELD_CHARGES]: setGlCodeToCharges(charges, glCodeMappings, invoiceCurrency),
      };


      setValues(newValues);
    },
  }),
  withComponentDidUpdatePropCallback({
    propName: 'glCodeMappings',
    callbackName: 'handleSetInitialGLCodes',
  }),
  withComponentDidUpdatePropCallback({
    propName: GC.FIELD_CURRENCY,
    callbackName: 'handleChangeCurrency',
  }),
  pure,
);

const withDefaultOrderAccessorials = compose(
  withHandlers({
    handleSetDefaultOrderAccessorials: (props) => () => {
      const {
        rateProps,
        isInvoice,
        isEditMode,
        addMXLineHaul,
        defaultOrderAccessorials,
        defaultOrderFuelAccessorial,
      } = props;

      const { values, setValues } = rateProps;

      const { charges } = values;

      if (R.and(R.not(isInvoice), G.isTrue(isEditMode))) return;

      const allAccessorials = R.concat(R.or(charges, []), R.or(defaultOrderAccessorials, []));

      let allCharges = G.addMainFuelChargeToCharges2(allAccessorials, defaultOrderFuelAccessorial);

      if (addMXLineHaul) {
        allCharges = R.append(
          R.assoc(
            GC.FIELD_DISPLAYED_VALUE,
            G.getWindowLocale('titles:mx-line-haul', 'MX Line Haul'),
            createMainCharge(),
          ),
          allCharges,
        );
      }

      const newValues = R.assoc(
        GC.FIELD_CHARGES,
        recalculateAllCharges(allCharges, props),
        values,
      );

      setValues(newValues);
    },
  }),
  withComponentDidUpdatePropCallback({
    propName: 'defaultOrderAccessorials',
    callbackName: 'handleSetDefaultOrderAccessorials',
  }),
  pure,
);

const isItemLengthMatrixCharge = R.propEq(
  GC.ACCESSORIAL_MATRIX_TYPE_ITEM_LENGTH,
  GC.FIELD_ACCESSORIAL_MATRIX_TYPE,
);

const replaceItemLengthMatrixCharges = (charges, sharedAccessorials) => R.concat(
  R.reject(isItemLengthMatrixCharge, R.or(charges, [])),
  R.filter(isItemLengthMatrixCharge, R.or(sharedAccessorials, [])),
);

const replaceAutoAccessorialCharges = (charges, sharedAccessorials) => R.concat(
  R.reject(G.isAutoAssessorialCharge, R.or(charges, [])),
  R.or(sharedAccessorials, []),
);

const matrixChargeFields = [
  GC.FIELD_ID,
  GC.FIELD_GUID,
  GC.FIELD_CHARGE_RATE,
  GC.FIELD_ACCESSORIAL_MATRIX_UOM,
  GC.FIELD_ACCESSORIAL_MATRIX_TYPE,
  GC.FIELD_ACCESSORIAL_MATRIX_ITEM_INFO,
  GC.FIELD_ACCESSORIAL_CONFIG_GUID,
  GC.FIELD_TRANSPORTATION_MODE_GUID,
];

const getMatrixChargesForComparison = (charges) => R.compose(
  R.map(R.pick(matrixChargeFields)),
  R.filter(isItemLengthMatrixCharge),
)(R.or(charges, []));

const shouldSuppressInitialMatrixCharges = (props) => {
  const { rateProps, isInvoice, isEditMode } = props;

  const allowItemLengthMatrixOnEdit = G.isTrue(R.prop('allowItemLengthMatrixOnEdit', rateProps));

  return G.isAllTrue(
    G.isTrue(isEditMode),
    R.not(isInvoice),
    R.not(allowItemLengthMatrixOnEdit),
  );
};

const withSharedAccessorialsApplyTo = compose(
  withHandlers({
    handleSetSharedAccessorialsApplyTo: (props) => () => {
      const { rateProps, isInvoice, isEditMode, sharedAccessorialsApplyTo } = props;

      const { values, setValues } = rateProps;

      const allowItemLengthMatrixOnEdit = G.isTrue(
        R.prop('allowItemLengthMatrixOnEdit', rateProps),
      );

      const { charges, transportationMode } = values;

      const filteredSharedApplyTo = G.filterChargesByModeOrNoMode(
        R.or(sharedAccessorialsApplyTo, []),
        transportationMode,
      );

      const editMode = R.and(R.not(isInvoice), G.isTrue(isEditMode));

      if (R.and(editMode, R.not(allowItemLengthMatrixOnEdit))) return;

      const allAccessorials = G.ifElse(
        editMode,
        replaceItemLengthMatrixCharges,
        replaceAutoAccessorialCharges,
      )(charges, filteredSharedApplyTo);

      const newValues = R.assoc(
        GC.FIELD_CHARGES,
        recalculateAllCharges(G.addMainFuelChargeToCharges(allAccessorials), props),
        values,
      );

      setValues(newValues);
    },
  }),
  withComponentDidUpdatePropCallback({
    propName: 'sharedAccessorialsApplyTo',
    callbackName: 'handleSetSharedAccessorialsApplyTo',
  }),
  pure,
);

const withItemLengthMatrixAccessorials = compose(
  withHandlers({
    handleSetItemLengthMatrixAccessorials: (props) => () => {
      const { rateProps, sharedAccessorialsApplyTo } = props;
      const { values, setValues } = rateProps;

      if (R.isNil(sharedAccessorialsApplyTo)) return;

      const charges = R.propOr([], GC.FIELD_CHARGES, values);

      const matrixAccessorials = G.filterChargesByModeOrNoMode(
        sharedAccessorialsApplyTo,
        R.prop(GC.FIELD_MODE, values),
      );

      if (R.equals(
        getMatrixChargesForComparison(charges),
        getMatrixChargesForComparison(matrixAccessorials),
      )) return;

      const chargesToUse = replaceItemLengthMatrixCharges(charges, matrixAccessorials);

      const newValues = R.assoc(
        GC.FIELD_CHARGES,
        recalculateAllCharges(chargesToUse, props),
        values,
      );

      setValues(newValues);
    },
  }),
  lifecycle({
    componentDidMount() {
      if (shouldSuppressInitialMatrixCharges(this.props)) return;

      this.props.handleSetItemLengthMatrixAccessorials();
    },
    componentDidUpdate(prevProps) {
      const previousAccessorials = R.prop('sharedAccessorialsApplyTo', prevProps);
      const currentAccessorials = R.prop('sharedAccessorialsApplyTo', this.props);
      const previousRecalculateId = R.prop('recalculateAllChargesId', prevProps);
      const currentRecalculateId = R.prop('recalculateAllChargesId', this.props);

      const accessorialsChanged = R.not(R.equals(previousAccessorials, currentAccessorials));
      const recalculateIdChanged = R.not(R.equals(previousRecalculateId, currentRecalculateId));

      const shouldSync = shouldSuppressInitialMatrixCharges(this.props)
        ? recalculateIdChanged
        : R.or(accessorialsChanged, recalculateIdChanged);

      if (shouldSync) {
        this.props.handleSetItemLengthMatrixAccessorials();
      }
    },
  }),
);

export const withDOCharges = compose(
  withItemLengthMatrixAccessorials,
  withRecalculateAllCharges,
  withRecalculateChargesOnChangeRecalculateId,
  withGLCodeMappings,
);

export const withDOCharges2 = compose(
  withItemLengthMatrixAccessorials,
  withRecalculateAllCharges,
  withRecalculateChargesOnChangeRecalculateId,
  withGLCodeMappings,
  withDefaultOrderAccessorials,
  withSharedAccessorialsApplyTo,
);
